import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateWebsiteBlogCategoryRequest,
  CreateWebsiteBlogCategoryRequestSchema,
  type UpdateWebsiteBlogCategoryRequest,
  UpdateWebsiteBlogCategoryRequestSchema,
  type WebsiteBlogCategoryListItem,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateBlogCategory, useUpdateBlogCategory } from '../use-blog-categories';

interface BlogCategoryFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  category?: WebsiteBlogCategoryListItem | null;
}

/** kebab-case the name for the slug default (lowercase, dashes, trimmed). */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const BlogCategoryFormDrawer = ({
  open,
  onOpenChange,
  mode,
  category,
}: BlogCategoryFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createMut = useCreateBlogCategory();
  const updateMut = useUpdateBlogCategory();
  const pending = createMut.isPending || updateMut.isPending;

  // Until the user hand-edits the slug, it mirrors the name (create only).
  const slugTouched = useRef(false);

  const createForm = useForm<CreateWebsiteBlogCategoryRequest>({
    resolver: zodResolver(CreateWebsiteBlogCategoryRequestSchema),
    defaultValues: { name: '', slug: '', sortOrder: 0, isActive: true },
  });
  const updateForm = useForm<UpdateWebsiteBlogCategoryRequest>({
    resolver: zodResolver(UpdateWebsiteBlogCategoryRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    // Editing: never auto-rewrite an existing slug.
    slugTouched.current = !isCreate;
    if (isCreate) {
      createForm.reset({ name: '', slug: '', sortOrder: 0, isActive: true });
    } else if (category) {
      updateForm.reset({
        name: category.name,
        slug: category.slug,
        sortOrder: category.sortOrder,
        isActive: category.isActive,
      });
    }
  }, [open, isCreate, category, createForm, updateForm]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = (isCreate ? createForm : updateForm) as ReturnType<typeof useForm<any>>;

  const onCreate = createForm.handleSubmit(async (values) => {
    try {
      await createMut.mutateAsync(values);
      onOpenChange(false);
    } catch {
      /* toast already fired in mutation hook */
    }
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!category) return;
    try {
      await updateMut.mutateAsync({
        id: category.id,
        payload: { ...values, expectedUpdatedAt: category.updatedAt },
      });
      onOpenChange(false);
    } catch {
      /* toast already fired */
    }
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'New category' : `Edit ${category?.name ?? 'category'}`}
      description="A blog/story category shown in the public story filters and the post editor."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Create category' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={isCreate ? onCreate : onUpdate}>
        <UzFormField name="name">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Name"
              placeholder="Traveler Diaries"
              required
              disabled={pending}
              error={error}
              onChange={(e) => {
                field.onChange(e);
                if (!slugTouched.current) form.setValue('slug', slugify(e.target.value));
              }}
            />
          )}
        </UzFormField>
        <UzFormField name="slug">
          {({ field, error }) => (
            <UzInput
              {...field}
              label="Identifier (slug)"
              placeholder="traveler-diaries"
              hint="Lowercase letters, digits and dashes. Used in URLs and story filters."
              required
              disabled={pending}
              error={error}
              onChange={(e) => {
                slugTouched.current = true;
                field.onChange(e);
              }}
            />
          )}
        </UzFormField>
        <UzFormField name="sortOrder">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              label="Sort order"
              value={field.value ?? 0}
              onValueChange={(v) => field.onChange(v ?? 0)}
              min={0}
              max={9999}
              step={10}
              hint="Lower values appear first in the filter list."
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="isActive">
          {({ field }) => (
            <div className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-zinc-900">Active</div>
                <div className="text-xs text-zinc-500">
                  Inactive categories are hidden from the public story filters and the editor
                  picker.
                </div>
              </div>
              <UzSwitch
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
                disabled={pending}
              />
            </div>
          )}
        </UzFormField>
      </UzForm>
    </UzDrawer>
  );
};
